import moment from 'moment';
import 'moment-timezone';
import type { LargeShiftDetailsProps } from '@tempmee/react-blocks';
import { IJob, IMultipleCounters } from '~/models';
import { formatMoney } from '~/utils/formatMoney';
import { computeHours, resolveProfessionName } from '~/utils/shiftCard';

/** Same fallback the legacy offer-expiration renderer uses when the office has no timezone. */
const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * A shift as these mappers need it — the route job, or a partial while it loads.
 *
 * `autofillOptions` is widened in rather than added to the shared model: it is a
 * raw backend field the auto-confirm row reads, and the legacy details widen it
 * the same way.
 */
export type TShiftLike =
  | (Partial<IJob> & { readonly autofillOptions?: { favorite?: boolean } })
  | null
  | undefined;

/**
 * Footer note for `LargeShiftDetails` on the Confirm Candidates pages, e.g.
 * "Offer expires today at 7:00 PM". Sentence case: the block renders it in a
 * `Label`, which uppercases its own text.
 *
 * Timezone-aware via `moment.tz` — an offer expiring at 7:00 PM local reads as a
 * different hour to an office in another zone, and "today" itself flips across
 * the date line. Returns `undefined` for a missing or unparseable expiration so
 * the caller simply omits the strip.
 */
export const formatOfferExpiry = (
  expiration?: Date | string | null,
  timezone?: string | null,
): string | undefined => {
  if (!expiration) {
    return undefined;
  }

  const expiresAt = moment.tz(expiration, timezone || DEFAULT_TIMEZONE);

  if (!expiresAt.isValid()) {
    return undefined;
  }

  const time = expiresAt.format('h:mm A');

  return expiresAt.isSame(moment.tz(timezone || DEFAULT_TIMEZONE), 'day')
    ? `Offer expires today at ${time}`
    : `Offer expires ${expiresAt.format('ddd, MMM DD, YYYY')} at ${time}`;
};

/**
 * The earliest expiration across the shift's counter offers.
 *
 * The summary block carries one expiry strip but every candidate holds their own
 * offer, so the list page has to pick. It picks the soonest: that is the deadline
 * the office actually has to beat, and overstating it would invite them to sit on
 * a decision that has already lapsed for someone.
 */
export const earliestOfferExpiry = (
  counters: readonly Pick<IMultipleCounters, 'expiration'>[] | null | undefined,
): Date | undefined => {
  const timestamps = (counters ?? [])
    .map((counter) => (counter?.expiration ? new Date(counter.expiration).getTime() : NaN))
    .filter((time) => !Number.isNaN(time));

  return timestamps.length > 0 ? new Date(Math.min(...timestamps)) : undefined;
};

/** The `LargeShiftDetails` props that come straight off the shift, with no page-level decisions. */
export type TShiftSummaryFields = Pick<
  LargeShiftDetailsProps,
  'date' | 'startTime' | 'endTime' | 'totalHours' | 'position' | 'rate'
>;

/**
 * Shift → the summary block's data props. `status` and `expiryNote` are left to
 * the page: they are the two values that depend on which state page is rendering
 * rather than on the shift alone.
 *
 * Every field is a string because the block is presentational — it formats
 * nothing. Times come pre-localised from the API (`local_start_time`), so no
 * conversion happens here; only the duration is derived.
 */
export const toShiftSummaryFields = (job: TShiftLike): TShiftSummaryFields => ({
  date: job?.local_date ?? '',
  startTime: job?.local_start_time ?? '',
  endTime: job?.local_end_time ?? '',
  totalHours: computeHours(job?.local_start_time, job?.local_end_time) ?? '',
  position: resolveProfessionName(job?.professionName, job?.subProfession) ?? '',
  rate: formatMoney(job?.rate),
});

/**
 * Shift → the rows of the Additional Information panel.
 *
 * These are the legacy Shift Details panel's non-money attributes: the money
 * moved to `Receipt`, and date/time/rate/position moved up into the summary
 * block, so what is left is the secondary detail an office skims rather than
 * decides on. Rows with nothing to show are dropped instead of rendering an
 * empty value, which would read as missing data rather than as not applicable.
 *
 * The note for the professional is NOT a row — it is the panel's own `notes`
 * slot, which the page owns because the confirm mutation submits it.
 */
